import type { Editor } from '@tiptap/react';
import type { TurnIntoCommand } from '../types/extension-interface';
import { documentHasH1 } from '../../../ui/tiptap/extensions/single-h1';

/**
 * Base turn-into configurations for built-in Tiptap extensions
 */
const BASE_TURN_INTO_CONFIGS: TurnIntoCommand[] = [
  {
    commandName: 'setParagraph',
    label: 'Text',
    icon: 'type',
    canTurnInto: (editor) => editor.can().setParagraph(),
    execute: (editor) => editor.chain().focus().setParagraph().run(),
  },
  {
    commandName: 'toggleHeading',
    label: 'Heading 1',
    icon: 'heading-1',
    canTurnInto: (editor) =>
      editor.can().toggleHeading({ level: 1 }) && !documentHasH1(editor), // Only allow if no h1 exists
    execute: (editor) =>
      editor.chain().focus().toggleHeading({ level: 1 }).run(),
  },
  {
    commandName: 'toggleHeading',
    label: 'Heading 2',
    icon: 'heading-2',
    canTurnInto: (editor) => editor.can().toggleHeading({ level: 2 }),
    execute: (editor) =>
      editor.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    commandName: 'toggleHeading',
    label: 'Heading 3',
    icon: 'heading-3',
    canTurnInto: (editor) => editor.can().toggleHeading({ level: 3 }),
    execute: (editor) =>
      editor.chain().focus().toggleHeading({ level: 3 }).run(),
  },
  {
    commandName: 'toggleBulletList',
    label: 'Bullet List',
    icon: 'list-bullet-alt',
    canTurnInto: (editor) => editor.can().toggleBulletList(),
    execute: (editor) => editor.chain().focus().toggleBulletList().run(),
  },
  {
    commandName: 'toggleOrderedList',
    label: 'Numbered List',
    icon: 'list-ordered',
    canTurnInto: (editor) => editor.can().toggleOrderedList(),
    execute: (editor) => editor.chain().focus().toggleOrderedList().run(),
  },
  {
    commandName: 'toggleTaskList',
    label: 'To-do List',
    icon: 'list-todo',
    canTurnInto: (editor) => editor.can().toggleTaskList(),
    execute: (editor) => editor.chain().focus().toggleTaskList().run(),
  },
  {
    commandName: 'wrapInBlockquote',
    label: 'Blockquote',
    icon: 'blockquote',
    canTurnInto: (editor) => editor.can().wrapIn('blockquote'),
    execute: (editor) => editor.chain().focus().wrapIn('blockquote').run(),
  },
  {
    commandName: 'toggleCodeBlock',
    label: 'Code Block',
    icon: 'code-block',
    canTurnInto: (editor) => editor.can().toggleNode('codeBlock', 'paragraph'),
    execute: (editor) =>
      editor.chain().focus().toggleNode('codeBlock', 'paragraph').run(),
  },
];

/**
 * Gets all turn-into configurations from the editor's extensions
 */
export function getTurnIntoConfigurations(
  editor: Editor | null
): TurnIntoCommand[] {
  if (!editor) {
    return [];
  }

  const configurations: TurnIntoCommand[] = [...BASE_TURN_INTO_CONFIGS];

  // Iterate through all extensions to find those with turn-into configuration
  editor.extensionManager.extensions.forEach((extension) => {
    // Check if extension has storage with getTurnIntoConfig
    if (
      extension.storage != null &&
      typeof extension.storage['getTurnIntoConfig'] === 'function'
    ) {
      try {
        const config = extension.storage['getTurnIntoConfig']();
        if (config != null && typeof config === 'object') {
          configurations.push(config);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(
          `Failed to get turn-into config for extension ${extension.name}:`,
          error
        );
      }
    }
  });

  return configurations;
}

/**
 * Gets turn-into configurations that are available for the current selection
 */
export function getAvailableTurnIntoConfigurations(
  editor: Editor | null
): TurnIntoCommand[] {
  if (!editor) {
    return [];
  }

  const allConfigs = getTurnIntoConfigurations(editor);

  return allConfigs.filter((config) => {
    try {
      return config.canTurnInto(editor);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        `Error checking if turn-into is available for ${config.label}:`,
        error
      );
      return false;
    }
  });
}
